/*
 * CustomizeWidgetsDialog — per-user "pick which widgets show/hide" control for
 * a MY DASHBOARD (a cloned prebuilt). Toggling saves to the caller's per-user
 * dashboard preferences (hidden_kpis / hidden_widgets) — never the shared
 * dashboard doc — so each person controls their own view.
 *
 * Only surfaced on clones (never the prebuilt template).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { SlidersHorizontal, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function CustomizeWidgetsDialog({ dashboardId, kpis = [], charts = [], prefs, onSaved }) {
    const [open, setOpen] = useState(false);
    const [hiddenKpis, setHiddenKpis] = useState(new Set());
    const [hiddenWidgets, setHiddenWidgets] = useState(new Set());
    const [saving, setSaving] = useState(false);

    // Seed local state from current prefs each time the dialog opens.
    useEffect(() => {
        if (!open) return;
        setHiddenKpis(new Set(prefs?.hidden_kpis || []));
        setHiddenWidgets(new Set(prefs?.hidden_widgets || []));
    }, [open, prefs]);

    const toggle = (set, setter, id) => {
        const next = new Set(set);
        if (next.has(id)) next.delete(id); else next.add(id);
        setter(next);
    };

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/dashboards/${dashboardId}/preferences`, {
                hidden_kpis: Array.from(hiddenKpis),
                hidden_widgets: Array.from(hiddenWidgets),
            });
            toast.success("View updated");
            onSaved?.(data);
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not save");
        } finally {
            setSaving(false);
        }
    };

    const Row = ({ id, title, checkedOn, onToggle, testid }) => (
        <label className="flex items-center justify-between gap-3 py-2 px-1 cursor-pointer" data-testid={testid}>
            <span className="text-sm truncate">{title || id}</span>
            <Switch checked={checkedOn} onCheckedChange={onToggle} />
        </label>
    );

    return (
        <>
            <Button size="sm" variant="outline" className="h-8 rounded-sm text-xs"
                    onClick={() => setOpen(true)} data-testid="dashboard-customize-btn">
                <SlidersHorizontal className="h-3.5 w-3.5 mr-1" /> Customize
            </Button>
            <Dialog open={open} onOpenChange={(v) => { if (!saving) setOpen(v); }}>
                <DialogContent className="max-w-md" data-testid="dashboard-customize-dialog">
                    <DialogHeader>
                        <DialogTitle>Customize widgets</DialogTitle>
                        <DialogDescription>Turn widgets on or off. Saved to your own view only.</DialogDescription>
                    </DialogHeader>
                    <div className="max-h-[55vh] overflow-auto">
                        {kpis.length > 0 && (
                            <>
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1 pt-1 pb-0.5">KPIs</div>
                                <div className="divide-y divide-border">
                                    {kpis.map((k) => (
                                        <Row key={k.id} id={k.id} title={k.title}
                                             checkedOn={!hiddenKpis.has(k.id)}
                                             onToggle={() => toggle(hiddenKpis, setHiddenKpis, k.id)}
                                             testid={`customize-kpi-${k.id}`} />
                                    ))}
                                </div>
                            </>
                        )}
                        {charts.length > 0 && (
                            <>
                                <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground px-1 pt-3 pb-0.5">Charts &amp; tables</div>
                                <div className="divide-y divide-border">
                                    {charts.map((w) => (
                                        <Row key={w.id} id={w.id} title={w.title}
                                             checkedOn={!hiddenWidgets.has(w.id)}
                                             onToggle={() => toggle(hiddenWidgets, setHiddenWidgets, w.id)}
                                             testid={`customize-widget-${w.id}`} />
                                    ))}
                                </div>
                            </>
                        )}
                        {kpis.length === 0 && charts.length === 0 && (
                            <div className="py-6 text-center text-sm text-muted-foreground">No widgets to customize.</div>
                        )}
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)} disabled={saving}>Cancel</Button>
                        <Button onClick={save} disabled={saving} data-testid="dashboard-customize-save">
                            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                            Save
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
